/**
 * The person's inbox across every project they can read, plus whatever is addressed to them:
 * what needs them first, then what is for them, then the rest. "What happened" opens on Mine
 * (records addressed to the person: changes to their tasks, outcomes of what they asked); the
 * whole news of their projects is one switch away. Unread is its own view (`?show=unread`, where the shell's bell
 * opens): exactly the records the count counts, newest first, whatever their lane.
 */

import { addressedTo, inboxLane } from '@aioe/shared';
import { Bell } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { InboxList, MarkAllRead, oneRecordPerTask } from '../components/Inbox.tsx';
import { Button, ErrorNote, Page, PageHeader, Stat } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad } from '../hooks.ts';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { listCount, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

type Filter = 'open' | 'needs-you' | 'unread' | 'log' | 'all';
type Scope = 'mine' | 'projects';
const FILTERS: readonly Filter[] = ['open', 'needs-you', 'unread', 'log', 'all'];
/** The most the platform counts as unread (its inbox read is 500 wide), so the Unread view holds every one it counts. */
const UNREAD_LIMIT = 500;

export function InboxPage() {
  const tone = VIEW_TONES.inbox!;
  // The view is in the address (`?show=unread`), so the bell and a link open it directly.
  const [params, setParams] = useSearchParams();
  const asked = params.get('show') as Filter | null;
  const filter: Filter = asked && FILTERS.includes(asked) ? asked : 'open';
  const setFilter = (f: Filter) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (f === 'open') n.delete('show');
        else n.set('show', f);
        return n;
      },
      { replace: true },
    );
  const [scope, setScope] = useState<Scope>('mine');
  const { account } = useAuth();
  // The newest 200, then older ones on request; a search reads titles and words on the server. The 30-second poll refreshes the first page only.
  const search = useListSearch();
  const inbox = usePagedList((cursor) => routes.inboxPage({ q: search.q, cursor, limit: 200 }), { q: search.q }, { intervalMs: 30_000, keyOf: (n) => `${n.projectId}/${n.id}` });
  // Unread is read on its own, filtered by the platform: every record the count counts, even one older than the newest 200.
  const unreadList = useLoad(() => (filter === 'unread' ? routes.inbox('unread', UNREAD_LIMIT) : Promise.resolve(undefined)), [filter], 30_000);
  const reload = () => {
    inbox.reload();
    if (filter === 'unread') unreadList.reload();
  };
  const items = oneRecordPerTask(inbox.items, account?.id);
  // One lane rule on every surface (inboxLanes.ts): the Inbox is what to look at; the log is news.
  const live = items.filter((n) => n.state === 'unread' || n.state === 'read');
  const open = live.filter((n) => inboxLane(n) === 'inbox');
  const news = items.filter((n) => inboxLane(n) === 'log');
  // Mine: what was addressed to this person. A task change is theirs when the task is theirs; the rest of the project's churn is news.
  const log = scope === 'mine' ? news.filter((n) => addressedTo(n, { id: account?.id })) : news;
  const needsYou = open.filter((n) => n.attention === 'needs-you');
  const unread = unreadList.data?.items ?? [];
  const unreadCount = unreadList.data?.unread ?? inbox.extra?.unread;
  const searching = search.q !== '';
  const shown = searching ? items : filter === 'all' ? items : filter === 'needs-you' ? needsYou : filter === 'unread' ? unread : filter === 'log' ? log : open;
  const title: Record<Filter, string> = { open: 'Inbox', 'needs-you': 'Needs you', unread: 'Unread', log: 'What happened', all: 'Everything' };
  return (
    <Page>
      <PageHeader tone={tone} icon={<Bell />} title="Inbox" subtitle="Approvals, decisions and questions from your projects, the same Inbox your workbench and phone show." actions={<Button size="sm" variant="ghost" onPress={reload}>Refresh</Button>} />
      <PartBoundary key="inbox" name="Inbox" tone={tone}>
      <ErrorNote error={inbox.error ?? unreadList.error} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Bell />} label="Needs you" value={needsYou.length} detail={needsYou.length ? (filter === 'needs-you' ? 'Showing only these' : 'Waiting for an answer') : 'Nothing waiting on you'} needsYou={needsYou.length > 0} onPress={() => setFilter(filter === 'needs-you' ? 'open' : 'needs-you')} selected={filter === 'needs-you'} />
        <Stat tone={tone} icon={<Bell />} label="Unread" value={unreadCount} detail={filter === 'unread' ? 'Showing only these' : unreadCount ? 'Press to show only these' : 'Across your projects'} onPress={() => setFilter(filter === 'unread' ? 'open' : 'unread')} selected={filter === 'unread'} />
        <Stat tone={tone} icon={<Bell />} label="Projects" value={new Set(items.map((n) => n.projectId)).size} detail="With something to show" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox label="Search the inbox" value={search.typed} onChange={search.setTyped} placeholder="Search inbox" className="w-56 max-w-full" />
      <div className="-mx-1 min-w-0 overflow-x-auto px-1 [scrollbar-width:none]">
        <Segment aria-label="Inbox filter" size="sm" selectedKey={filter} onSelectionChange={(k) => setFilter(String(k) as Filter)} className="w-fit">
          <Segment.Item id="open">Open</Segment.Item>
          <Segment.Item id="needs-you">
            Needs you
            {needsYou.length ? <span className="section-heading__count">{needsYou.length}</span> : null}
          </Segment.Item>
          <Segment.Item id="unread">
            Unread
            {unreadCount ? <span className="section-heading__count">{unreadCount}</span> : null}
          </Segment.Item>
          <Segment.Item id="log">What happened</Segment.Item>
          <Segment.Item id="all">Everything</Segment.Item>
        </Segment>
      </div>
      </div>
      <InboxList
        items={shown}
        onChange={reload}
        showProject
        selectable
        title={searching ? 'Search results' : title[filter]}
        description={searching ? listCount(items.length, { one: 'record', many: 'records' }, { hasMore: inbox.hasMore, searched: true }) + ` matching "${search.q}"` : filter === 'unread' ? 'Everything you have not opened yet, newest first, from every project and lane.' : filter !== 'log' ? undefined : scope === 'mine' ? 'Changes to your tasks and news addressed to you. Nothing here waits on you.' : 'News from your projects: every task change, review and placement. Nothing here waits on you.'}
        actions={
          <>
            <MarkAllRead items={shown} unread={unreadCount} onChange={reload} />
            {filter === 'log' && (
              <Segment aria-label="Whose news" size="sm" selectedKey={scope} onSelectionChange={(k) => setScope(String(k) as Scope)}>
                <Segment.Item id="mine">Mine</Segment.Item>
                <Segment.Item id="projects">Everything in my projects</Segment.Item>
              </Segment>
            )}
          </>
        }
        {...(searching ? { order: 'newest' as const, emptyTitle: inbox.loading ? 'Searching' : `Nothing matches "${search.q}"`, emptyBody: inbox.loading ? '' : 'Try fewer words, or clear the search to see your inbox again.' } : {})}
        {...(!searching && filter === 'unread' ? { order: 'newest' as const, emptyTitle: unreadList.data ? 'Nothing unread' : 'Loading what is unread', emptyBody: unreadList.data ? 'You have opened everything. New approvals, decisions and news show here until you open them.' : '' } : {})}
        {...(!searching && filter === 'log' && scope === 'mine' ? { emptyTitle: 'Nothing about your work', emptyBody: 'Changes to tasks given to you, and news addressed to you, show here. Everything in my projects shows the rest of your projects’ news.' } : {})}
      />
      {inbox.hasMore && filter !== 'unread' ? <ShowMore older onPress={inbox.showMore} pending={inbox.loadingMore} /> : null}
      </PartBoundary>
    </Page>
  );
}
